import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import t from "./locales";
import i18n from "../i18n";
import { parseDate } from "../utils/dateHelper";
import { isSameDay } from "date-fns";
import { getImage } from "../utils/pathHelper";

import { flatten, uniq, sleep } from "../utils/commonHelper";
import Modal from "../Shared/Modal";
import menuIcon from "./images/line_icon/menu.svg";
import styles from "./styles/index.module.scss";

import { ILesson, IFilterEntry, IResort } from "./types";
import LessonList from "./LessonSchoolItemList";
import Pagination from "../Shared/Pagination";
import { usePagination } from "../Shared/usePagination";
import Button from "../Shared/Button";

interface ILessonSchoolItemSearchProps {
	lessonItems: any;
	localCurrency: any;
}

export const LessonSchoolItemSearch: React.FC<ILessonSchoolItemSearchProps> = ({
	lessonItems,
	localCurrency,
}) => {
	const FILTER_ID_AVAILABILITY = "availability";
	const FILTER_ID_RESORT_CODE = "resortCode";
	const FILTER_ID_PARTICIPANT_COUNT = "participantCount";
	const LESSONS_PER_PAGE = 8;
	const allLessons = lessonItems.map((lesson) => ({
		...lesson,
		[FILTER_ID_AVAILABILITY]: lesson[FILTER_ID_AVAILABILITY].map((dateString) =>
			parseDate(dateString),
		),
	}));

	// Local states
	const allDate = uniq(
		flatten(allLessons.map((lesson) => lesson.availability)),
	);
	// In case we need search by resort
	const allResorts = uniq(
		flatten(allLessons.map((lesson) => lesson.resorts)),
		"resortCode",
	);
	const [filteredLessons, setFilteredLessons] = useState<ILesson[]>(allLessons);
	const [filters, setFilters] = useState<IFilterEntry[]>([]);
	const [appliedFilters, setAppliedFilters] = useState<IFilterEntry[]>([]);
	const [isModalShown, setModalShown] = useState<boolean>(false);
	const [isLoading, setLoading] = useState<boolean>(false);

	// usePaginationフックを使用
	const {
		page,
		currentItems: currentLessons,
		totalItems,
		itemsPerPage,
		hasNext,
		hasPrev,
		handlePageChange: paginationHandlePageChange,
		reset: resetPagination,
	} = usePagination<ILesson>(filteredLessons, LESSONS_PER_PAGE);
	const participantOptions = useMemo(() => {
		const counts = allLessons
			.map((lesson) => lesson.participantCount)
			.filter(Number.isFinite);

		const minCount = counts.length ? Math.min(...counts) : 0;
		const maxCount = counts.length ? Math.max(...counts) : 0;
		const options = [];
		for (let i = minCount; i <= maxCount; i++) {
			options.push(i);
		}

		return options;
	}, [allLessons]);

	useEffect(() => {
		const resortCode = new URLSearchParams(window.location.search).get(
			"resort_code",
		);
		if (resortCode) {
			const filter: IFilterEntry = {
				filterId: FILTER_ID_RESORT_CODE,
				selectedValues: [resortCode],
			};
			setFilters([filter]);
			updateCurrentLessons([filter]);
		}
	}, []);

	function lessonsFilteredBy(filterEntry: IFilterEntry): Set<ILesson> {
		const { filterId, selectedValues } = filterEntry;
		let result: ILesson[] = [];
		if (filterId === FILTER_ID_AVAILABILITY) {
			result = allLessons.filter((lesson) =>
				selectedValues.some((value) =>
					lesson[FILTER_ID_AVAILABILITY].some((date) => isSameDay(date, value)),
				),
			);
		} else if (filterId === FILTER_ID_RESORT_CODE) {
			result = allLessons.filter((lesson) =>
				selectedValues.some((value) =>
					lesson.resorts.some((resort) => resort.resortCode === value),
				),
			);
		} else if (filterId === FILTER_ID_PARTICIPANT_COUNT) {
			result = allLessons.filter((lesson) =>
				selectedValues.some(
					(value) => lesson.participantCount === Number(value),
				),
			);
		} else {
			result = allLessons.filter((lesson) =>
				selectedValues.some((value) => lesson[filterId].indexOf(value) !== -1),
			);
		}
		return new Set(result);
	}

	function updateCurrentLessons(applyingFilters: IFilterEntry[] = filters) {
		let result: ILesson[] = [...allLessons];

		if (applyingFilters.length > 0) {
			applyingFilters.forEach((filterEntry: IFilterEntry) => {
				result = [...result].filter((lesson) =>
					lessonsFilteredBy(filterEntry).has(lesson),
				);
			});
		}

		setFilteredLessons(result);
		resetPagination();
		setAppliedFilters(applyingFilters);
	}

	// ページ変更時にローディング表示を追加するラッパー
	const handlePageChange = useCallback(
		async (newPage: number) => {
			setLoading(true);
			await sleep(300);
			paginationHandlePageChange(newPage);
			setLoading(false);
		},
		[paginationHandlePageChange],
	);

	function updateFilter(input: IFilterEntry) {
		const index = filters.findIndex(
			(existing) => existing.filterId === input.filterId,
		);
		const isRemoval = input.selectedValues.every((value) => value === "");
		if (isRemoval) {
			if (index === -1) {
				// Do nothing
			} else {
				const newFilters = [...filters];
				newFilters.splice(index, 1);
				setFilters(newFilters);
			}
		} else {
			if (index === -1) {
				setFilters([...filters, input]);
			} else {
				const newFilter = [...filters];
				newFilter[index] = input;
				setFilters(newFilter);
			}
		}
	}

	function handleClickFilterOption(
		filterId: IFilterEntry["filterId"],
		selectedValues: IFilterEntry["selectedValues"],
	) {
		if (
			filters.find(
				(filter) =>
					filter.filterId === filterId &&
					filter.selectedValues.some((value) => selectedValues.includes(value)),
			)
		) {
			updateFilter({
				filterId,
				selectedValues: filters
					.find((filter) => filter.filterId === filterId)
					.selectedValues.filter((value) => !selectedValues.includes(value)),
			});
		} else {
			updateFilter({ filterId, selectedValues });
		}
	}

	async function handleSelectChange(filterEntry: IFilterEntry) {
		setLoading(true);
		const newFilter = {
			filterId: filterEntry.filterId,
			selectedValues: [filterEntry.selectedValues[0]],
		};
		updateFilter(newFilter);
		setLoading(false);
	}

	function handleApplyFilters() {
		setModalShown(false);
		updateCurrentLessons();
	}

	function selectedValueLabel(filterId: string, value: string) {
		if (filterId === "resortCode") {
			return allResorts.find((resort) => resort.resortCode === value)
				?.resortName;
		}
		if (filterId === "participantCount") {
			return Number(value) === 1
				? i18n.t("base.number_of_people.one")
				: i18n.t("base.number_of_people.other", { count: Number(value) });
		}
		if (filterId === "languages") {
			return i18n.t(`lessons.show.language.${value}`);
		}
		if (filterId === "subjects") {
			return t[value]();
		}
		if (filterId === "participantTypes") {
			return t[value]();
		}
		return value;
	}

	function handleClearAllFilters() {
		setFilters([]);
	}

	const selectedValueCounts = appliedFilters
		.map((filter) => filter.selectedValues)
		.flat().length;

	return (
		<div>
			<div className={styles.filterContainer}>
				<div
					className={styles.filterButton}
					onClick={() => setModalShown(true)}
				>
					<img src={getImage(menuIcon)} alt="menu" width={16} height={16} />
					{`${i18n.t("lessons.show.filter")}${selectedValueCounts > 0 ? `(${selectedValueCounts})` : ""}`}
				</div>
				{appliedFilters.map((appliedFilter) =>
					appliedFilter.selectedValues.map((value) => (
						<div
							key={`${appliedFilter.filterId}-${value}`}
							className={styles.filterOptionValue}
						>
							{selectedValueLabel(appliedFilter.filterId, value)}
						</div>
					)),
				)}
			</div>
			<LessonList
				currentLessons={currentLessons}
				localCurrency={localCurrency}
				isLoading={isLoading}
			/>
			<Pagination
				page={page}
				hasNext={hasNext}
				hasPrev={hasPrev}
				totalItems={totalItems}
				itemsPerPage={itemsPerPage}
				handlePageChange={handlePageChange}
			/>
			<Modal
				isShown={isModalShown}
				handleClose={() => {
					setModalShown(false);
				}}
			>
				<div className={styles.filterModal}>
					<div className={styles.filterOptionTitle}>{t.participantCount()}</div>
					<div className={styles.filterOptionList}>
						{participantOptions.map((count) => {
							const isSelected = filters.find(
								(filter) =>
									filter.filterId === "participantCount" &&
									filter.selectedValues.includes(count),
							);
							return (
								<div
									key={count}
									onClick={() =>
										handleClickFilterOption("participantCount", [count])
									}
									className={`${styles.filterOption}
										${isSelected ? styles.selected : ""}`}
								>
									{count === 1
										? i18n.t("base.number_of_people.one")
										: i18n.t("base.number_of_people.other", { count })}
								</div>
							);
						})}
					</div>

					<div className={styles.filterOptionTitle}>{t.skiResort()}</div>
					<div className={styles.filterOptionList}>
						{allResorts.map((resort: IResort) => {
							const isSelected = filters.find(
								(filter) =>
									filter.filterId === "resortCode" &&
									filter.selectedValues.includes(resort.resortCode),
							);
							return (
								<div
									key={resort.resortCode}
									onClick={() =>
										handleClickFilterOption("resortCode", [resort.resortCode])
									}
									className={`${styles.filterOption}
											${isSelected ? styles.selected : ""}`}
								>
									{resort.resortName}
								</div>
							);
						})}
					</div>

					<div className={styles.filterOptionTitle}>{t.language()}</div>
					<div className={styles.filterOptionList}>
						{["english", "chinese", "japanese"].map((language) => {
							const isSelected = filters.find(
								(filter) =>
									filter.filterId === "languages" &&
									filter.selectedValues.includes(language),
							);
							const isDisabled = !allLessons.some((lesson) =>
								lesson.languages.includes(language),
							);
							return (
								<div
									key={language}
									onClick={() =>
										isDisabled
											? null
											: handleClickFilterOption("languages", [language])
									}
									className={`${styles.filterOption}
										${isSelected ? styles.selected : ""} ${isDisabled ? styles.disabled : ""}`}
								>
									{i18n.t(`lessons.show.language.${language}`)}
								</div>
							);
						})}
					</div>

					<div className={styles.filterOptionTitle}>{t.subject()}</div>
					<div className={styles.filterOptionList}>
						{["ski", "snowboard"].map((subject) => {
							const isSelected = filters.find(
								(filter) =>
									filter.filterId === "subjects" &&
									filter.selectedValues.includes(subject),
							);
							const isDisabled = !allLessons.some((lesson) =>
								lesson.subjects.includes(subject),
							);
							return (
								<div
									key={subject}
									onClick={() =>
										isDisabled
											? null
											: handleClickFilterOption("subjects", [subject])
									}
									className={`${styles.filterOption}
										${isSelected ? styles.selected : ""} ${isDisabled ? styles.disabled : ""}`}
								>
									{t[subject]()}
								</div>
							);
						})}
					</div>

					<div className={styles.filterOptionTitle}>{t.participantType()}</div>
					<div className={styles.filterOptionList}>
						{["private", "group"].map((participantType) => {
							const isSelected = filters.find(
								(filter) =>
									filter.filterId === "participantTypes" &&
									filter.selectedValues.includes(participantType),
							);
							const isDisabled = !allLessons.some((lesson) =>
								lesson.participantTypes.includes(participantType),
							);
							return (
								<div
									key={participantType}
									onClick={() =>
										isDisabled
											? null
											: handleClickFilterOption("participantTypes", [
													participantType,
												])
									}
									className={`${styles.filterOption}
										${isSelected ? styles.selected : ""} ${isDisabled ? styles.disabled : ""}`}
								>
									{t[participantType]()}
								</div>
							);
						})}
					</div>

					<div className={styles.filterModalFooter}>
						<div className={styles.filterModalFooterButtons}>
							<Button
								className={styles.filterModalClearAllButton}
								href="#"
								onClick={handleClearAllFilters}
							>
								{i18n.t("lessons.show.clear_all")}
							</Button>
							<Button
								className={styles.filterModalApplyButton}
								href="#"
								onClick={handleApplyFilters}
							>
								{i18n.t("lessons.show.filter")}
							</Button>
						</div>

						<Button
							className={styles.filterModalCloseButton}
							href="#"
							onClick={() => {
								setModalShown(false);
							}}
						>
							{i18n.t("lessons.show.close")}
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
};
